import React, { useState, useCallback } from 'react';
import _ from 'lodash';
import { LwBaseModal, useDualUserData } from './topModalCommon';
import { RenderGroupNameDisplay } from './nameDisplay';
import { RenderSearchCondition } from './searchCondition';
import { RenderGroupUserDisplay } from './usersDisplay';
import { RenderGroupRegisterFooter, RenderGroupEditFooter } from './footer';
import { useErrorInfomation } from './errorInfomation';
import {
  AppendableUsers,
  AssignUsers,
  GroupName,
} from '../../services/lwUser/Group';
import { swapLwUsersBetweenLwUserList } from '../../services/lwUser/Base';
import { NotImplementedError } from '../../services/lwUser/Error';
import { SearchCondition } from '../../services/lwUser/SearchCondition';

/**
 * グループ登録時のモーダル
 * @param {number} fieldId 現場ID
 * @param {string} fieldName 現場名
 * @param {function} clickBack 戻るボタンを押下した時に動作する関数
 * @param {function} clickSubmit 送信ボタンを押下した時に動作する関数
 **/
function LwGroupRegisterModal({ fieldId, fieldName, clickBack, clickSubmit }) {
  const [groupName, setGroupName] = useState(new GroupName(fieldName));
  const [searchCondition, setSearchCondition] = useState(new SearchCondition());
  const [tempSearchCondition, setTempSearchCondition] = useState(new SearchCondition());
  const [isLoading, setIsLoading] = useState(false);
  const { displayErrorInfomation, RenderErrorInfomation } = useErrorInfomation(
    clickBack
  );
  const getAppendableUsers = useCallback(
    async () => {
      return await AppendableUsers.createByApiData(fieldId, searchCondition).finally(() => {
        setIsLoading(false);
      });
    },
    [fieldId, searchCondition]
  );
  const getAssignUsers = useCallback(() => {
    return new AssignUsers([]);
  }, []);
  const {
    searchedleftUsers: searchedleftUserData,
    searchedrightUsers,
  } = useDualUserData(
    getAppendableUsers,
    getAssignUsers,
    displayErrorInfomation
  );
  const searchedleftUsers =
    _.get(searchedleftUserData, 'appendableUsers', new AppendableUsers([])) ||
    new AppendableUsers([]);
  const isSso = _.get(searchedleftUserData, 'isSso', false) || false;
  const [leftMovedUserIds, setLeftMovedUserIds] = useState([]);
  const [rightMovedUserIds, setRightMovedUserIds] = useState([]);

  const {
    new_first_lw_users: appendableUsers,
    new_second_lw_users: assignUsers,
  } = swapLwUsersBetweenLwUserList(
    searchedleftUsers,
    searchedrightUsers,
    rightMovedUserIds,
    leftMovedUserIds
  );

  return (
    <React.Fragment>
      <LwBaseModal
        topClassName={'dialog--full LwEditModal'}
        title={'グループの新規登録'}
        renderBody={
          <React.Fragment>
            <RenderGroupNameDisplay
              fieldName={fieldName}
              groupName={groupName}
              setGroupName={setGroupName}
            />
            <RenderSearchCondition
              fieldId={fieldId}
              setSearchCondition={setSearchCondition}
              tempSearchCondition={tempSearchCondition}
              setTempSearchCondition={setTempSearchCondition}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
            />
            <RenderGroupUserDisplay
              appendableUsers={appendableUsers}
              assignUsers={assignUsers}
              isSso={isSso}
              setLeftMovedUserIds={setLeftMovedUserIds}
              setRightMovedUserIds={setRightMovedUserIds}
            />
          </React.Fragment>
        }
        renderFooter={
          <React.Fragment>
            <RenderGroupRegisterFooter
              fieldId={fieldId}
              groupName={groupName}
              registerlwUserIds={rightMovedUserIds}
              clickBack={clickBack}
              clickSubmit={clickSubmit}
              displayErrorInfomation={displayErrorInfomation}
            />
          </React.Fragment>
        }
        renderErrorInfomation={<RenderErrorInfomation />}
      />
    </React.Fragment>
  );
}

/**
 * グループ更新時のモーダル
 * @param {boolean} visible モーダルを表示するか
 * @param {number} fieldId 現場ID
 * @param {string} fieldName 現場名
 * @param {number} lwGroupId グループID
 * @param {string} lwGroupName グループ名
 * @param {function} clickBack 戻るボタンを押下した時に動作する関数
 * @param {function} clickSubmit 送信ボタンを押下した時に動作する関数
 **/
function LwGroupEditModal({
  fieldId,
  fieldName,
  lwGroupId,
  lwGroupName,
  clickBack,
  clickSubmit,
}) {
  const [groupName, setGroupName] = useState(new GroupName(lwGroupName));
  const [searchCondition, setSearchCondition] = useState(new SearchCondition());
  const [tempSearchCondition, setTempSearchCondition] = useState(new SearchCondition());
  const [isLoading, setIsLoading] = useState(false);
  const { displayErrorInfomation, RenderErrorInfomation } = useErrorInfomation(
    clickBack
  );
  const getAppendableUsers = useCallback(
    async () => {
      return await AppendableUsers.createByApiData(fieldId, searchCondition).finally(() => {
        setIsLoading(false);
      });
    },
    [fieldId, searchCondition]
  );
  const getAssignUsers = useCallback(
    async () => {
      return await AssignUsers.createByApiData(
        fieldId,
        lwGroupId,
        searchCondition
      );
    },
    [fieldId, lwGroupId, searchCondition]
  );
  const {
    searchedleftUsers: searchedleftUserData,
    searchedrightUsers,
  } = useDualUserData(
    getAppendableUsers,
    getAssignUsers,
    displayErrorInfomation
  );
  const searchedleftUsers =
    _.get(searchedleftUserData, 'appendableUsers', new AppendableUsers([])) ||
    new AppendableUsers([]);
  const isSso = _.get(searchedleftUserData, 'isSso', false) || false;
  // 追加可能ユーザーに既にグループに所属しているユーザーがいるため取り除く
  let leftUsers;
  try {
    leftUsers = searchedleftUsers.removeUsers(searchedrightUsers.ids);
  } catch (e) {
    if (e instanceof NotImplementedError) {
      leftUsers = searchedleftUsers;
    } else {
      throw e;
    }
  }
  const [leftMovedUserIds, setLeftMovedUserIds] = useState([]);
  const [rightMovedUserIds, setRightMovedUserIds] = useState([]);

  const {
    new_first_lw_users: appendableUsers,
    new_second_lw_users: assignUsers,
  } = swapLwUsersBetweenLwUserList(
    leftUsers,
    searchedrightUsers,
    rightMovedUserIds,
    leftMovedUserIds
  );
  return (
    <LwBaseModal
      topClassName={'dialog--full LwEditModal'}
      title={'グループの編集'}
      renderBody={
        <React.Fragment>
          <RenderGroupNameDisplay
            fieldName={fieldName}
            groupName={groupName}
            setGroupName={setGroupName}
          />
          <RenderSearchCondition
            fieldId={fieldId}
            setSearchCondition={setSearchCondition}
            tempSearchCondition={tempSearchCondition}
            setTempSearchCondition={setTempSearchCondition}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
          />
          <RenderGroupUserDisplay
            appendableUsers={appendableUsers}
            assignUsers={assignUsers}
            isSso={isSso}
            setLeftMovedUserIds={setLeftMovedUserIds}
            setRightMovedUserIds={setRightMovedUserIds}
          />
        </React.Fragment>
      }
      renderFooter={
        <React.Fragment>
          <RenderGroupEditFooter
            fieldId={fieldId}
            lwGroupId={lwGroupId}
            groupName={groupName}
            rightMovedUserIds={rightMovedUserIds}
            leftMovedUserIds={leftMovedUserIds}
            clickBack={clickBack}
            clickSubmit={clickSubmit}
            displayErrorInfomation={displayErrorInfomation}
          />
        </React.Fragment>
      }
      renderErrorInfomation={<RenderErrorInfomation />}
    />
  );
}

export { LwGroupRegisterModal, LwGroupEditModal };



// WEBPACK FOOTER //
// ./src/components/lwUser/topModalGroup.jsx